import React, { MouseEvent as ReactMouseEvent, useMemo, useState } from "react";

import { defineMessages } from "@ai360/core/intl";
import classnames from "classnames";

import { FormattingHelpers } from "@ai360/core";

import { PencilIcon, TrashcanIcon } from "~/core/icons";

import PlainTableHeader from "./components/plain-table-header";
import TableCell from "../data-table/components/table-cell";

import "./zero-to-n-grid.css";
import { IColumnData, IIndex, IRecord } from "../interfaces";
import { SortDirectionType } from "react-virtualized";
import { useIntl } from "react-intl";
import { useDetectOutsideClick } from "~/core/components/use-detect-outside-click";

const messages = defineMessages({
    delete: { id: "plainGrid.delete", defaultMessage: "Delete" },
    edit: { id: "plainGrid.edit", defaultMessage: "Edit" },
});

export interface IZeroToInfiniteGridProps {
    className?: string;
    columns?: Record<string, Partial<IColumnData>>;
    disabled?: boolean;
    onDelete?: (record: IRecord, index: IIndex) => void;
    onEdit?: (record: IRecord, index: IIndex) => void;
    records?: any[] | IRecord[];
    showHeader?: boolean;
    formatCellValue?: (value: string) => React.ReactElement;
    formatColumnKeys?: string[];
}

const comparer = (i, j) => (isNaN(Number(i)) ? i.localeCompare(j) : i - j);

function getSortedRecords(
    records: IRecord[],
    sortDirection: SortDirectionType,
    sortColumnName: string,
    formatNumber: (value) => string
): IRecord[] {
    return records.sort((a, b) => {
        // Get thousands separator based on locale formatting
        const separator = formatNumber("1000")[1];
        const getComparisonValue = (o) => {
            const val = o[sortColumnName];
            if (typeof val === "string") {
                const asNumber = FormattingHelpers.parseStringToNumber(val, separator);
                return !isNaN(asNumber) ? asNumber : val;
            } else if (typeof val !== "object") {
                return val;
            } else {
                console.error(
                    `Attempted to sort grid by ${sortColumnName} which is a ${typeof val}`
                );
            }
        };

        const x = getComparisonValue(a);
        const y = getComparisonValue(b);
        return sortDirection === "ASC" ? comparer(x, y) : comparer(y, x);
    });
}

export default function ZeroToInfiniteGrid(props: IZeroToInfiniteGridProps) {
    const { className, columns, onDelete, onEdit, showHeader = true } = props;
    const { formatMessage, formatNumber } = useIntl();

    const [selectedRow, setSelectedRow] = useState<IRecord>({
        canDelete: null,
        modifiedDate: null,
    });
    const [sortDirection, setSortDirection] = useState<SortDirectionType>(null);
    const [sortColumnName, setSortColumnName] = useState<string>(null);
    const gridRef = useDetectOutsideClick(() =>
        setSelectedRow({ canDelete: null, modifiedDate: null })
    );

    const records = useMemo(
        () =>
            (sortDirection && sortColumnName
                ? getSortedRecords(props.records ?? [], sortDirection, sortColumnName, formatNumber)
                : props.records ?? []
            ).map<IRecord>((r) => ({
                canDelete: null,
                modifiedDate: null,
                ...r,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.records, sortDirection, sortColumnName]
    );

    function onRowSelection(event: ReactMouseEvent, newSelectedRow: IRecord): void {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        if (props.disabled) {
            setSelectedRow(null);
            return;
        }
        if (newSelectedRow === selectedRow) {
            setSelectedRow(null);
        } else {
            setSelectedRow(newSelectedRow);
        }
    }

    function sortRecords({ sortOptions, sortColumnName }): void {
        const { sortDirection } = sortOptions;
        setSortDirection(sortDirection);
        setSortColumnName(sortColumnName);
    }

    function getCells(record: IRecord) {
        const columnKeys = Object.keys(columns);
        return columnKeys?.map((attr) => {
            const value = record[attr];
            if (columns[attr].printer) {
                return columns[attr].printer(value, record);
            }
            let valueComponent = <p>{value}</p>;
            if (
                props.formatCellValue &&
                props.formatColumnKeys &&
                props.formatColumnKeys.includes(attr)
            ) {
                valueComponent = props.formatCellValue(value);
            }
            return (
                <TableCell
                    key={`row-${attr}`}
                    className={columns[attr].className}
                    title={`${value}`}
                >
                    {valueComponent}
                </TableCell>
            );
        });
    }

    function renderEditDeleteButtons(record: IRecord, index: IIndex, selected: boolean): IRecord[] {
        const retObj = [];
        if (onDelete) {
            retObj.push(
                <TableCell
                    key="zton-delete"
                    className={"z-n-icon"}
                    onClick={() => {
                        if (selected && record.canDelete !== false) {
                            onDelete(record, index);
                        }
                    }}
                    title={formatMessage(messages.delete)}
                >
                    {!selected || record.canDelete === false ? null : <TrashcanIcon />}
                </TableCell>
            );
        }
        if (onEdit) {
            retObj.push(
                <TableCell
                    key="zton-edit"
                    className={"z-n-icon"}
                    onClick={() => {
                        if (selected && record.doNotEdit !== true) {
                            onEdit(record, index);
                        }
                    }}
                    title={formatMessage(messages.edit)}
                >
                    {!selected || record.doNotEdit === true ? null : (
                        <PencilIcon className="pencil-icon" />
                    )}
                </TableCell>
            );
        }
        return retObj;
    }

    return (
        <div className={classnames("plain-data-table", className)} ref={gridRef}>
            {!showHeader ? null : (
                <PlainTableHeader
                    columns={columns}
                    sortRecords={sortRecords}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            )}
            <div className="plain-table-body">
                {records
                    ?.filter((record) => record)
                    .map((record, index) => {
                        const selected = selectedRow === record;
                        const selectedClass = selected ? "data-table-row-selected" : "";
                        const { restrictEditDelete } = record;
                        const restrictModeClass =
                            restrictEditDelete && onDelete ? "data-table-row-restrict" : "";

                        return (
                            <div
                                key={`z-n-row-${index}`}
                                className={classnames(
                                    "data-table-row",
                                    selectedClass,
                                    restrictModeClass
                                )}
                                onClick={(event) => onRowSelection(event, record)}
                            >
                                {restrictEditDelete
                                    ? null
                                    : renderEditDeleteButtons(record, index as IIndex, selected)}
                                {getCells(record)}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
